import { useAuth0 } from '@auth0/auth0-react';
import { Banner, Button, Modal, RichTextContent, focusRing } from '@ths/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import {
  useAcceptHotelPortalTermsMutation,
  useMyHotelPortalTermsStatusQuery,
} from '../generated/graphql';

import { PORTAL_TERMS_REQUIRED_EVENT, PortalTermsContext } from './context';

export function PortalTermsGate({ children }: { children: ReactNode }) {
  const { user } = useAuth0();
  return (
    <ActorTermsGate key={user?.sub ?? 'anonymous'}>{children}</ActorTermsGate>
  );
}

function ActorTermsGate({ children }: { children: ReactNode }) {
  const { isAuthenticated, logout } = useAuth0();
  const location = useLocation();
  const publicTerms =
    location.pathname === '/terms' || location.pathname.startsWith('/terms/');
  const status = useMyHotelPortalTermsStatusQuery({
    skip: !isAuthenticated || publicTerms,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const { refetch } = status;
  const [accept, acceptance] = useAcceptHotelPortalTermsMutation();
  const [error, setError] = useState('');
  const state =
    status.data?.myHotelPortalTermsStatus ??
    status.previousData?.myHotelPortalTermsStatus;
  const published = state?.published;
  const required = !!published && state?.requiresAcceptance === true;
  const blocked = !publicTerms && isAuthenticated && required;
  const review = () => {
    void refetch().catch(() => {});
  };

  useEffect(() => {
    if (!isAuthenticated || publicTerms) return;
    const check = () => {
      if (document.visibilityState === 'visible')
        void refetch().catch(() => {});
    };
    const requireTerms = () => {
      void refetch().catch(() => {});
    };
    window.addEventListener('focus', check);
    window.addEventListener(PORTAL_TERMS_REQUIRED_EVENT, requireTerms);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.removeEventListener('focus', check);
      window.removeEventListener(PORTAL_TERMS_REQUIRED_EVENT, requireTerms);
      document.removeEventListener('visibilitychange', check);
    };
  }, [isAuthenticated, publicTerms, refetch]);

  async function acceptTerms() {
    if (!state?.published || status.loading || acceptance.loading) return;
    setError('');
    try {
      await accept({ variables: { versionId: state.published.id } });
      await refetch();
    } catch {
      setError(
        'Acceptance could not be saved. Review the current terms and try again.',
      );
      void refetch().catch(() => {});
    }
  }
  if (!isAuthenticated || publicTerms) return <>{children}</>;
  return (
    <PortalTermsContext.Provider
      value={{ requiresAcceptance: required, review }}
    >
      {children}
      {published && (
        <Modal
          open={blocked}
          dismissible={false}
          onOpenChange={() => {}}
          title={published.title}
          description="Review and accept these terms to continue using the portal."
          className="max-w-3xl"
          footer={
            <div className="flex w-full flex-wrap items-center gap-3">
              <Button
                type="button"
                loading={acceptance.loading}
                disabled={status.loading || !!status.error}
                onClick={() => void acceptTerms()}
              >
                Accept and continue
              </Button>
              <Button
                type="button"
                variant="link"
                onClick={() =>
                  logout({ logoutParams: { returnTo: window.location.origin } })
                }
              >
                Sign out
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <p className="text-caption text-text-secondary">
              Version {published.versionNumber} · Published{' '}
              {new Date(published.publishedAt).toLocaleDateString()}
            </p>
            {published.changeSummary && (
              <Banner status="info">{published.changeSummary}</Banner>
            )}
            <RichTextContent value={published.content} />
            {status.error && (
              <Banner status="error">
                Terms could not be refreshed.{' '}
                <Button variant="link" onClick={review}>
                  Try again
                </Button>
              </Banner>
            )}
            {error && <Banner status="error">{error}</Banner>}
            <div>
              <a
                href="https://www.teamhousing.com/contact/"
                className={`text-accent underline ${focusRing}`}
              >
                Contact THS
              </a>
            </div>
          </div>
        </Modal>
      )}
    </PortalTermsContext.Provider>
  );
}
